import { toJalaliString } from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, formatDateShort, formatMoney, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getDailyReport } from "../../api.js";

type ViewTab = "screen" | "print";

export function DailyReportPage() {
  const todayIso = useMemo(() => {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  }, []);

  const [date, setDate] = useState<string>(todayIso);
  const [tab, setTab] = useState<ViewTab>("screen");

  const query = useQuery({
    queryKey: ["daily-report", date],
    queryFn: () => getDailyReport(date || undefined),
  });

  const report = query.data;
  const jalaliDate = useMemo(() => (date ? toJalaliString(date) : ""), [date]);

  const methodLabel: Record<string, string> = {
    cash: "نقدی",
    card: "کارت‌خوان",
    "bank-transfer": "حواله بانکی",
    cheque: "چک صیادی",
  };

  return (
    <div className="stack" style={{ gap: "var(--s5)" }}>
      <style>{`
        @media print {
          .no-print, header.top, nav.nav, aside.rail, .top-bar {
            display: none !important;
          }
          body, #root, .app-shell, .main {
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .a4-sheet {
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 5mm !important;
          }
        }
        .a4-sheet {
          background: #fff;
          color: #111;
          border-radius: var(--r-card);
          border: 1px solid var(--line);
          padding: var(--s6);
          box-shadow: var(--sh-card);
        }
        .a4-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid var(--line);
          padding-bottom: var(--s4);
          margin-bottom: var(--s5);
        }
        .report-section {
          margin-bottom: var(--s5);
        }
        .report-section-title {
          font-size: var(--fs-18);
          font-weight: 700;
          margin-bottom: var(--s3);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: var(--fs-14);
        }
        .report-table th, .report-table td {
          border: 1px solid var(--line);
          padding: var(--s2) var(--s3);
          text-align: right;
        }
        .report-table th {
          background: var(--canvas);
          font-weight: 600;
        }
        .report-table tfoot td {
          background: var(--canvas);
          font-weight: 700;
        }
        .sig-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--s6);
          margin-top: var(--s6);
          padding-top: var(--s4);
        }
        .sig-box {
          border-top: 1px dashed var(--line);
          padding-top: var(--s3);
          text-align: center;
          color: var(--muted);
          font-size: var(--fs-14);
        }
      `}</style>

      {/* Controls Bar */}
      <div
        className="no-print row wrap"
        style={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--s4)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "var(--fs-28)", fontWeight: 700, margin: 0 }}>گزارش پایان روز</h1>
          <p className="muted" style={{ margin: "var(--s1) 0 0", fontSize: "var(--fs-14)" }}>
            خلاصه عملیات، فروش، بهای تمام‌شده، مصرف انبار و مبالغ دریافتی روز
          </p>
        </div>

        <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
          {/* Day Picker */}
          <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
            <span style={{ fontSize: "var(--fs-14)", fontWeight: 500 }}>تاریخ:</span>
            <input
              type="date"
              className="ctl"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
              }}
              style={{
                padding: "var(--s2) var(--s3)",
                borderRadius: "var(--r-ctl)",
                border: "1px solid var(--line)",
                background: "var(--card)",
                fontFamily: "inherit",
              }}
            />
            <Button
              variant="default"
              onClick={() => {
                setDate(todayIso);
              }}
            >
              امروز
            </Button>
          </div>

          {/* Mode Switch with Seg */}
          <Seg<ViewTab>
            label="حالت نمایش گزارش"
            value={tab}
            onChange={setTab}
            options={[
              { value: "screen", label: "نمای صفحه" },
              { value: "print", label: "برگه A4" },
            ]}
          />

          {/* Print Button */}
          <Button
            variant="primary"
            onClick={() => {
              window.print();
            }}
          >
            چاپ A4
          </Button>
        </div>
      </div>

      {/* Query States */}
      {query.isLoading && (
        <Card>
          <div style={{ padding: "var(--s6)", textAlign: "center" }}>
            <p className="muted">در حال دریافت اطلاعات روز...</p>
          </div>
        </Card>
      )}

      {query.isError && (
        <Card>
          <div
            style={{
              padding: "var(--s6)",
              textAlign: "center",
              color: "var(--coral-ink)",
            }}
          >
            <p>خطا در دریافت گزارش پایان روز. لطفاً دسترسی خود را بررسی فرمایید.</p>
          </div>
        </Card>
      )}

      {report && (
        <div className="a4-sheet" dir="rtl">
          {/* Sheet Header */}
          <div className="a4-header">
            <div>
              <h2
                style={{
                  fontSize: "var(--fs-20)",
                  fontWeight: 700,
                  margin: 0,
                  color: "var(--ink)",
                }}
              >
                آشپزخانه مرکزی تبریز
              </h2>
              <p className="muted" style={{ fontSize: "var(--fs-14)", margin: "var(--s1) 0 0" }}>
                گزارش عملکرد و تسویه پایان روز
              </p>
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: "var(--fs-16)", fontWeight: 700 }}>
                تاریخ: {faDigits(report.jalaliDate || jalaliDate)}
              </div>
              <div className="muted" style={{ fontSize: "var(--fs-12)", marginTop: "2px" }}>
                میلادی: {formatDateShort(report.date)}
              </div>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <div
            className="no-print row wrap"
            style={{
              gap: "var(--s3)",
              marginBottom: "var(--s5)",
            }}
          >
            <div className="card" style={{ flex: "1 1 180px", padding: "var(--s4)" }}>
              <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                کل پرس توزیع‌شده
              </span>
              <div
                style={{
                  fontSize: "var(--fs-24)",
                  fontWeight: 700,
                  marginTop: "var(--s1)",
                }}
              >
                {faDigits(report.sales.totalPortions)} پرس
              </div>
            </div>

            <div className="card" style={{ flex: "1 1 180px", padding: "var(--s4)" }}>
              <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                کل فروش روز
              </span>
              <div
                style={{
                  fontSize: "var(--fs-24)",
                  fontWeight: 700,
                  marginTop: "var(--s1)",
                }}
              >
                {formatMoney(report.sales.totalRevenueRial, "rial")}
              </div>
            </div>

            {report.canSeeCost && report.profit && (
              <div className="card" style={{ flex: "1 1 180px", padding: "var(--s4)" }}>
                <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                  سود ناخالص برآوردی
                </span>
                <div
                  style={{
                    fontSize: "var(--fs-24)",
                    fontWeight: 700,
                    marginTop: "var(--s1)",
                    color: report.profit.grossProfitRial < 0 ? "var(--coral-ink)" : "var(--ink)",
                  }}
                >
                  {formatMoney(report.profit.grossProfitRial, "rial")}
                </div>
              </div>
            )}

            <div className="card" style={{ flex: "1 1 180px", padding: "var(--s4)" }}>
              <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                کل وجوه دریافتی
              </span>
              <div
                style={{
                  fontSize: "var(--fs-24)",
                  fontWeight: 700,
                  marginTop: "var(--s1)",
                }}
              >
                {formatMoney(report.moneyReceived.totalReceivedRial, "rial")}
              </div>
            </div>
          </div>

          {/* Section 1: Sales of the Day */}
          <div className="report-section">
            <div className="report-section-title">
              <span>۱. فروش روزانه به تفکیک سازمان</span>
              <span className="muted" style={{ fontSize: "var(--fs-14)" }}>
                مجموع فروش: {formatMoney(report.sales.totalRevenueRial, "rial")}
              </span>
            </div>

            {report.sales.organizations.length === 0 ? (
              <p className="muted" style={{ fontSize: "var(--fs-14)" }}>
                سفارشی برای این روز ثبت نشده است.
              </p>
            ) : (
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>ردیف</th>
                    <th>نام سازمان / واحد طرف حساب</th>
                    <th style={{ width: "120px", textAlign: "center" }}>تعداد پرس</th>
                    <th style={{ width: "180px", textAlign: "left" }}>مبلغ فروش (ریال)</th>
                    <th style={{ width: "120px", textAlign: "center" }}>وضعیت قیمت</th>
                  </tr>
                </thead>
                <tbody>
                  {report.sales.organizations.map((org, idx) => (
                    <tr key={org.organizationId}>
                      <td style={{ textAlign: "center" }}>{faDigits(idx + 1)}</td>
                      <td style={{ fontWeight: 600 }}>{org.organizationName}</td>
                      <td style={{ textAlign: "center" }}>{faDigits(org.portions)}</td>
                      <td style={{ textAlign: "left", fontWeight: 600 }}>
                        {formatMoney(org.revenueRial, "rial")}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {org.missingPricePortions > 0 ? (
                          <Chip tone="coral">بدون قیمت ({faDigits(org.missingPricePortions)})</Chip>
                        ) : (
                          <span className="muted">کامل</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ textAlign: "right" }}>
                      جمع کل فروش روز
                    </td>
                    <td style={{ textAlign: "center" }}>{faDigits(report.sales.totalPortions)}</td>
                    <td style={{ textAlign: "left" }}>
                      {formatMoney(report.sales.totalRevenueRial, "rial")}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {report.sales.totalMissingPricePortions > 0 && (
                        <Chip tone="coral">
                          بدون قیمت ({faDigits(report.sales.totalMissingPricePortions)})
                        </Chip>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Section 2: Estimated Gross Profit */}
          {report.canSeeCost && report.profit && (
            <div className="report-section">
              <div className="report-section-title">
                <span>۲. برآورد بهای تمام‌شده و سود ناخالص</span>
                <span
                  style={{
                    fontSize: "var(--fs-14)",
                    color: report.profit.grossProfitRial < 0 ? "var(--coral-ink)" : "var(--ink)",
                  }}
                >
                  سود ناخالص: {formatMoney(report.profit.grossProfitRial, "rial")} ( حاشیه:{" "}
                  {faDigits(Math.round(report.profit.marginPerMille / 10))}٪)
                </span>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    <th>شاخص مالی</th>
                    <th style={{ width: "220px", textAlign: "left" }}>مبلغ (ریال)</th>
                    <th>توضیحات</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>درآمد فروش روز</td>
                    <td style={{ textAlign: "left", fontWeight: 600 }}>
                      {formatMoney(report.profit.revenueRial, "rial")}
                    </td>
                    <td className="muted">بر مبنای فاکتور قطعی روز</td>
                  </tr>
                  <tr>
                    <td>بهای تمام‌شده غذاهای توزیع‌شده</td>
                    <td style={{ textAlign: "left", fontWeight: 600 }}>
                      {formatMoney(report.profit.costRial, "rial")}
                    </td>
                    <td className="muted">از اسنپ‌شات هزینه‌ی انبار روز</td>
                  </tr>
                  {report.profit.upgradeCostRial > 0 && (
                    <tr>
                      <td>هزینه‌ی پرس ویژه (ارتقای آشپزخانه)</td>
                      <td style={{ textAlign: "left", color: "var(--coral-ink)" }}>
                        − {formatMoney(report.profit.upgradeCostRial, "rial")}
                      </td>
                      <td className="muted">هزینه‌ی مازاد بر عهده آشپزخانه</td>
                    </tr>
                  )}
                  {report.profit.missingCostPortions > 0 && (
                    <tr>
                      <td>اقلام بدون بهای تمام‌شده</td>
                      <td style={{ textAlign: "left" }}>—</td>
                      <td>
                        <Chip tone="coral">
                          بدون بهای تمام‌شده ({faDigits(report.profit.missingCostPortions)} پرس)
                        </Chip>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      color: report.profit.grossProfitRial < 0 ? "var(--coral-ink)" : "inherit",
                    }}
                  >
                    <td>سود ناخالص روز</td>
                    <td style={{ textAlign: "left" }}>
                      {formatMoney(report.profit.grossProfitRial, "rial")}
                    </td>
                    <td>میانگین به ازای هر پرس: {formatMoney(report.profit.profitPerPortionRial, "rial")}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Section 3: Stock Used */}
          <div className="report-section">
            <div className="report-section-title">
              <span>۳. مواد مصرفی و ضایعات انبار</span>
              {report.canSeeCost && (
                <span className="muted" style={{ fontSize: "var(--fs-14)" }}>
                  {report.stockUsed.totalStockValueRial !== null
                    ? `ارزش کل: ${formatMoney(report.stockUsed.totalStockValueRial, "rial")}`
                    : "ارزش کل: دارای اقلام بدون قیمت"}
                </span>
              )}
            </div>

            {report.stockUsed.items.length === 0 ? (
              <p className="muted" style={{ fontSize: "var(--fs-14)" }}>
                حواله مصرف یا ضایعاتی برای این روز ثبت نشده است.
              </p>
            ) : (
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>ردیف</th>
                    <th style={{ width: "100px" }}>کد کالا</th>
                    <th>نام ماده اولیه / کالا</th>
                    <th style={{ width: "80px", textAlign: "center" }}>واحد</th>
                    <th style={{ width: "100px", textAlign: "center" }}>حواله مصرف</th>
                    <th style={{ width: "100px", textAlign: "center" }}>ضایعات</th>
                    <th style={{ width: "100px", textAlign: "center" }}>کل مصرف</th>
                    {report.canSeeCost && (
                      <>
                        <th style={{ width: "120px", textAlign: "left" }}>نرخ واحد (ریال)</th>
                        <th style={{ width: "140px", textAlign: "left" }}>ارزش کل (ریال)</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {report.stockUsed.items.map((item, idx) => (
                    <tr key={item.itemId}>
                      <td style={{ textAlign: "center" }}>{faDigits(idx + 1)}</td>
                      <td>{faDigits(item.itemCode)}</td>
                      <td style={{ fontWeight: 600 }}>{item.itemName}</td>
                      <td style={{ textAlign: "center" }}>{item.baseUnit}</td>
                      <td style={{ textAlign: "center" }}>
                        {faDigits((item.issueQuantityMilli / 1000).toLocaleString("fa-IR"))}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {faDigits((item.wasteQuantityMilli / 1000).toLocaleString("fa-IR"))}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>
                        {faDigits((item.totalQuantityMilli / 1000).toLocaleString("fa-IR"))}
                      </td>
                      {report.canSeeCost && (
                        <>
                          <td style={{ textAlign: "left" }}>
                            {item.unitPriceRial !== null ? (
                              formatMoney(item.unitPriceRial, "rial")
                            ) : (
                              <Chip tone="coral">بدون قیمت</Chip>
                            )}
                          </td>
                          <td style={{ textAlign: "left", fontWeight: 600 }}>
                            {item.totalValueRial !== null ? (
                              formatMoney(item.totalValueRial, "rial")
                            ) : (
                              <Chip tone="coral">بدون قیمت</Chip>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: "right" }}>
                      تعداد اسناد: {faDigits(report.stockUsed.issueVouchersCount)} حواله مصرف،{" "}
                      {faDigits(report.stockUsed.wasteVouchersCount)} سند ضایعات
                    </td>
                    <td colSpan={3} style={{ textAlign: "center" }}>
                      جمع کل اقلام
                    </td>
                    {report.canSeeCost && (
                      <td colSpan={2} style={{ textAlign: "left" }}>
                        {report.stockUsed.totalStockValueRial !== null ? (
                          formatMoney(report.stockUsed.totalStockValueRial, "rial")
                        ) : (
                          <Chip tone="coral">بدون قیمت</Chip>
                        )}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Section 4: Money Received */}
          <div className="report-section">
            <div className="report-section-title">
              <span>۴. دریافتی‌ها و گردش نقدینگی</span>
              <span className="muted" style={{ fontSize: "var(--fs-14)" }}>
                کل وصولی: {formatMoney(report.moneyReceived.totalReceivedRial, "rial")}
              </span>
            </div>

            {/* Methods Breakdown Pills */}
            <div className="row wrap" style={{ gap: "var(--s3)", marginBottom: "var(--s3)" }}>
              {report.moneyReceived.methods.map((m) => (
                <div
                  key={m.method}
                  style={{
                    background: "var(--canvas)",
                    padding: "var(--s2) var(--s4)",
                    borderRadius: "var(--r-pill)",
                    border: "1px solid var(--line)",
                    fontSize: "var(--fs-14)",
                  }}
                >
                  <span className="muted">{methodLabel[m.method] ?? m.method}: </span>
                  <span style={{ fontWeight: 600 }}>{formatMoney(m.totalAmountRial, "rial")}</span>
                  <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                    {" "}
                    ({faDigits(m.count)} فقره)
                  </span>
                </div>
              ))}
            </div>

            {report.moneyReceived.payments.length === 0 ? (
              <p className="muted" style={{ fontSize: "var(--fs-14)" }}>
                دریافتی یا پرداختی در این روز ثبت نشده است.
              </p>
            ) : (
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>ردیف</th>
                    <th>نام سازمان / واریزکننده</th>
                    <th style={{ width: "120px", textAlign: "center" }}>روش پرداخت</th>
                    <th style={{ width: "160px", textAlign: "left" }}>مبلغ (ریال)</th>
                    <th style={{ width: "140px" }}>شماره پیگیری / ارجاع</th>
                    <th>مشخصات چک / اسناد</th>
                    <th>توضیحات</th>
                  </tr>
                </thead>
                <tbody>
                  {report.moneyReceived.payments.map((p, idx) => (
                    <tr key={p.paymentId}>
                      <td style={{ textAlign: "center" }}>{faDigits(idx + 1)}</td>
                      <td style={{ fontWeight: 600 }}>{p.organizationName}</td>
                      <td style={{ textAlign: "center" }}>{methodLabel[p.method] ?? p.method}</td>
                      <td style={{ textAlign: "left", fontWeight: 600 }}>
                        {formatMoney(p.amountRial, "rial")}
                      </td>
                      <td>{p.referenceNumber ? faDigits(p.referenceNumber) : "—"}</td>
                      <td>
                        {p.method === "cheque" ? (
                          <span>
                            چک {p.chequeNumber ? faDigits(p.chequeNumber) : ""}{" "}
                            {p.chequeBank ? `بانک ${p.chequeBank}` : ""}{" "}
                            {p.chequeDueDate ? `سررسید: ${formatDateShort(p.chequeDueDate)}` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="muted">{p.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      مجموع کل مبالغ وصول‌شده روز
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {formatMoney(report.moneyReceived.totalReceivedRial, "rial")}
                    </td>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      {report.moneyReceived.chequesCount > 0 && (
                        <span>
                          شامل {faDigits(report.moneyReceived.chequesCount)} فقره چک به ارزش{" "}
                          {formatMoney(report.moneyReceived.chequesTotalRial, "rial")}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Signatures Footer */}
          <div className="sig-grid">
            <div className="sig-box">
              <span>امضا و تأیید مدیر آشپزخانه / سرپرست شیفت</span>
            </div>
            <div className="sig-box">
              <span>امضا و تأیید امور مالی و حسابداری</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
